import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarDays, Percent, Plus, Trash2, PoundSterling } from 'lucide-react';
import { toast } from 'sonner';
import { getCustomerPaymentTerms, updateCustomerPaymentTerms } from '../services/api';

const TERM_LABELS = {
  '7d': 'Net 7',
  '14d': 'Net 14',
  '30d': 'Net 30',
  '30eom': 'Net 30 (end of month)',
};

/**
 * Inline editor for a customer's payment terms + early-payment discount
 * tiers. Lives inside the Customer Detail page; saves directly to the
 * backend via PATCH.
 *
 * The early-discount mechanic: when an invoice issued under this
 * customer's terms is paid within `withinDays`, a credit at `percent`
 * is queued and prepended on the customer's NEXT invoice as a
 * negative line (description: "Discount for invoice IN20260000X
 * (Paid in N days — X%)").
 */
export const CustomerPaymentTermsCard = ({ customerId }) => {
  const [terms, setTerms] = useState('30d');
  const [tiers, setTiers] = useState([]);
  const [allowed, setAllowed] = useState(['7d', '14d', '30d', '30eom']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCustomerPaymentTerms(customerId);
        if (cancelled) return;
        setTerms(data.paymentTerms || '30d');
        setTiers(data.earlyPaymentDiscounts || []);
        setAllowed(data.allowedTerms || ['7d', '14d', '30d', '30eom']);
      } catch (e) {
        toast.error('Could not load payment terms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const setTermsAndDirty = (v) => { setTerms(v); setDirty(true); };
  const updateTier = (idx, patch) => {
    setTiers((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const addTier = () => {
    setTiers((rows) => [...rows, { withinDays: 7, percent: 2.5 }]);
    setDirty(true);
  };
  const removeTier = (idx) => {
    setTiers((rows) => rows.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty / NaN rows before submitting — backend will also
      // sanitise but keep the UX tight.
      const cleaned = tiers
        .map((t) => ({
          withinDays: Number(t.withinDays) || 0,
          percent: Number(t.percent) || 0,
        }))
        .filter((t) => t.withinDays > 0 && t.percent > 0);
      const res = await updateCustomerPaymentTerms(customerId, {
        paymentTerms: terms,
        earlyPaymentDiscounts: cleaned,
      });
      setTerms(res.paymentTerms);
      setTiers(res.earlyPaymentDiscounts);
      setDirty(false);
      toast.success('Payment terms saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save payment terms');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
        Loading payment terms…
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-4"
      data-testid="customer-payment-terms-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <PoundSterling className="h-4 w-4 text-slate-500" />
            Payment terms
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Sets the due-date offset and any early-payment discount for
            this customer's auto-billed invoices.
          </p>
        </div>
        {dirty && (
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="payment-terms-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="payment-terms-select" className="text-xs uppercase tracking-wider text-slate-500">
          Net days
        </Label>
        <Select value={terms} onValueChange={setTermsAndDirty}>
          <SelectTrigger id="payment-terms-select" className="w-full" data-testid="payment-terms-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowed.map((v) => (
              <SelectItem key={v} value={v} data-testid={`payment-terms-option-${v}`}>
                {TERM_LABELS[v] || v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wider text-slate-500">
            Early-payment discount tiers
          </Label>
          <button
            type="button"
            onClick={addTier}
            data-testid="payment-terms-add-tier"
            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
          >
            <Plus className="h-3 w-3" /> Add tier
          </button>
        </div>
        {tiers.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No discount tiers configured. Add one to credit the customer
            on their next invoice when they pay early.
          </p>
        ) : (
          <ul className="space-y-2">
            {tiers.map((t, idx) => (
              <li
                key={idx}
                className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end"
                data-testid={`payment-terms-tier-${idx}`}
              >
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" /> Within days
                  </Label>
                  <Input
                    type="number" min="1" step="1"
                    value={t.withinDays}
                    onChange={(e) => updateTier(idx, { withinDays: e.target.value })}
                    data-testid={`payment-terms-tier-days-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Percent className="h-3 w-3" /> Discount
                  </Label>
                  <Input
                    type="number" min="0.01" max="100" step="0.01"
                    value={t.percent}
                    onChange={(e) => updateTier(idx, { percent: e.target.value })}
                    data-testid={`payment-terms-tier-percent-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeTier(idx)}
                  data-testid={`payment-terms-tier-remove-${idx}`}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-rose-700 hover:bg-rose-50 border border-slate-200"
                  title="Remove tier"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CustomerPaymentTermsCard;
